import type { CandidateInterview, InterviewRecord } from '../../../fsm/jobs';
import { apiInstantToDate } from '../../../utils/apiInstant';

/**
 * The words the recruiter page uses for Rio's interview record (avrio-rio#254, served by avrio-backend#308): how an interview
 * ended, what the page recorded while it ran, and whether it was scored. The wording follows the iPhone app's employer view
 * (avrio-mobile `src/interviews/record.ts`), so the two say the same thing.
 *
 * Integrity events are stated as facts ("left the page 2 times"), never as a verdict: leaving a page has many ordinary causes
 * and the record is context for the person reading it, not a finding about the candidate.
 */

/** The end reason of an interview the server ended because the candidate left the interview page twice. */
export const LEFT_INTERVIEW_TWICE = 'left_interview_twice';

/** How the interview ended, in plain words, or null for an open interview or a reason this page has not seen. */
export const endedReasonLabel = (
  reason: string | null | undefined,
): string | null => {
  switch (reason) {
    case 'plan_finished':
      return 'Rio asked all its questions';
    case 'candidate_finished':
      return 'The candidate said they were finished';
    case LEFT_INTERVIEW_TWICE:
      return 'Ended because the candidate left the interview page twice';
    default:
      return null;
  }
};

/** What one integrity event means, in plain words. */
export const eventLabel = (event: string | null | undefined): string => {
  switch (event) {
    case 'left':
      return 'Left the interview page';
    case 'returned':
      return 'Returned to the interview';
    case 'paste_blocked':
      return 'A paste was blocked';
    default:
      return 'Interview event';
  }
};

const times = (count: number): string =>
  count === 1 ? 'once' : `${count} times`;

/** A non-negative whole count, or 0 for anything else. */
const countOf = (value: unknown): number =>
  typeof value === 'number' && Number.isInteger(value) && value > 0 ? value : 0;

/**
 * What the interview page recorded, as short facts: "Left the page 2 times", "Pasting was blocked once". Empty when
 * there was nothing to record.
 */
export const integrityFacts = (
  record: InterviewRecord | null | undefined,
): string[] => {
  const leaves = countOf(record?.leaveCount);
  const pastes = countOf(record?.pasteBlockedCount);
  const facts: string[] = [];
  if (leaves > 0) facts.push(`Left the page ${times(leaves)}`);
  if (pastes > 0) facts.push(`Pasting was blocked ${times(pastes)}`);
  return facts;
};

/** "under a minute", "17 min", "1 h 5 min"; null without both ends, or with an end before the start. */
export const durationLabel = (
  start: number | string | null | undefined,
  end: number | string | null | undefined,
): string | null => {
  const from = apiInstantToDate(start);
  const to = apiInstantToDate(end);
  if (!from || !to || to.getTime() < from.getTime()) return null;
  const minutes = Math.round((to.getTime() - from.getTime()) / 60000);
  if (minutes < 1) return 'under a minute';
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  return rest === 0 ? `${hours} h` : `${hours} h ${rest} min`;
};

/** Whether the server ended the interview (a reason is recorded), whatever its status says: one ended before a scored answer keeps its old status. */
export const isInterviewEnded = (
  interview: Pick<CandidateInterview, 'record'> | null | undefined,
): boolean => Boolean(interview?.record?.endedReason);

/**
 * Whether the interview finished (completed, or ended) with nothing scored. Rio writes `unscored: true` and no
 * `interviewScore` for it, never a 0. A completed row with no score and no rescore reads the same, so a payload without
 * the flag still says so. A score that is present always wins: an interview that has a score was scored.
 */
export const isInterviewUnscored = (
  interview: CandidateInterview | null | undefined,
  hasRescore: boolean,
): boolean => {
  if (!interview || hasRescore) return false;
  if (typeof interview.interviewScore === 'number') return false;
  return interview.unscored === true || interview.completed;
};
